<template>
  <div>
    <div ref="container" class="chart-container" />
  </div>
</template>

<script>
/* eslint-disable */
// Provides a set of high contrast colors.
// Currently only used in pie charts as per customer request
const highContrastColors = [
  '#e6194b',
  '#3cb44b',
  '#ffe119',
  '#4363d8',
  '#f58231',
  '#911eb4',
  '#46f0f0',
  '#f032e6',
  '#bcf60c',
  '#fabebe',
  '#008080',
  '#e6beff',
  '#9a6324',
  '#fffac8',
  '#800000',
  '#aaffc3',
  '#808000',
  '#ffd8b1',
  '#000075',
  '#808080',
  '#ffffff',
  '#000000',
];

// Provides a set of colors based on a gradient from color1 to color2, based on the number of colors needed (factor)
function interpolateColor(color1, color2, factor) {
  if (arguments.length < 3) {
    factor = 0.5;
  }
  const result = color1.slice();
  for (let i = 0; i < 3; i++) {
    result[i] = Math.round(result[i] + factor * (color2[i] - color1[i]));
  }
  return result;
}

function interpolateColors(color1, color2, steps) {
  if (steps == 1) {
    return [color1];
  }
  const stepFactor = 1 / (steps - 1);
  const interpolatedColorArray = [];
  color1 = color1.match(/\d+/g).map(Number);
  color2 = color2.match(/\d+/g).map(Number);
  for (let i = 0; i < steps; i++) {
    interpolatedColorArray.push(
      fullColorHex(interpolateColor(color1, color2, stepFactor * i))
    );
  }
  return interpolatedColorArray;
}

function rgbToHex(rgb) {
  let hex = Number(rgb).toString(16);
  if (hex.length < 2) {
    hex = `0${hex}`;
  }
  return hex;
}

function fullColorHex(arr) {
  const red = rgbToHex(arr[0]);
  const green = rgbToHex(arr[1]);
  const blue = rgbToHex(arr[2]);
  return `#${red}${green}${blue}`;
}

export default {
  name: 'ReportChart',
  props: {
    data: {
      type: Array,
      required: true,
    },
    type: {
      type: String,
      default: 'bar',
      required: false,
    },
    charttitle: {
      type: String,
      required: true,
    },
    limit: {
      type: Number,
      required: false,
      default: null,
    },
    colors: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      chart: null,
    };
  },
  watch: {
    data() {
      this.render();
    },
  },
  mounted() {
    this.render();
  },
  destroyed() {
    this.chart.destroy();
    this.canvas.width = 0;
    this.canvas.height = 0;
  },
  methods: {
    render() {
      if (!this.data) return;
      let displayData = {...this.data};
      
      // Some data is an object (like cuts per hout of day).  Convert this to an array
      if (Object.prototype.toString.call(displayData) != '[object Array]' && typeof(displayData) === 'object') {
        displayData = Object.values(displayData);
      }
      
      const holder = this.$refs.container;
      holder.innerHTML = '';
      this.canvas = document.createElement('canvas');
      holder.appendChild(this.canvas);
      const ctx = this.canvas.getContext('2d');

      // If a pie chart, append labels with the relevant percentages
      if (this.type == 'pie') {
        let totalCount = 0;
        displayData.forEach(row => {
          totalCount += row.count;
        });
        displayData.forEach(row => {
          row.name = row.name.split(' - ')[0];
          row.name += ` - ${((100 * row.count) / totalCount).toFixed(2)}%`;
        });
      }

      // If a limit is to be applied (e.g. top 10 results only), sort by count then slice to the limit
      if (this.limit) {
        const limit = this.limit * 1;
        displayData = displayData
          .sort((a, b) => b.count - a.count)
          .slice(0, limit);
      }

      let colorSet = interpolateColors(
        'rgb(94, 79, 162)',
        'rgb(247, 148, 89)',
        displayData.length
      );
      if (this.type == 'pie') {
        colorSet = highContrastColors;
      }
      if (this.colors) {
        // Custom colors
        colorSet = displayData.map(r => {
          const key = r.name.split(' - ')[0];
          return this.colors[key];
        });
      }
      this.chart = new Chart(ctx, {
        type: this.type,
        data: {
          labels: displayData.map(r => r.name),
          datasets: [
            {
              label: this.charttitle,
              backgroundColor: colorSet, // (this.type == "pie") ? highContrastColors : interpolateColors("rgb(94, 79, 162)", "rgb(247, 148, 89)", displayData.length)
              data: displayData.map(r => r.count),
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          legend: {
            display: this.type != 'bar' && this.type != 'line',
          },
          gridLines: {
            drawBorder: false,
          },
          layout: {
            padding: {
              left: 20,
              right: 20,
              top: 20,
              bottom: 20,
            },
          },
          scales: this.type != 'pie' && {
            yAxes: [
              {
                display: true,
                ticks: {
                  suggestedMin: 0,
                  precision: 0,
                },
              },
            ],
          },
        },
      });
    },
  },
};
</script>
<style scoped>
.chart-container {
  width: 100%;
  border: solid 1px #efefef;
  position: relative;
  min-height: 430px;
}
@media (max-width: 576px) {
  .chart-container {
    min-height: 350px;
  }
}
</style>
